import { Rgb24Converter } from './Rgb24Reader';
import { decodePng16Wasm } from './tiffWasm';
export class PngReader {
    constructor() {
        this.config = {
            pngScaleFactor: 1000, // Default: millimeters to meters
            invalidValue: 0,
        };
    }
    canRead(filename) {
        return filename.toLowerCase().endsWith('.png');
    }
    setConfig(config) {
        this.config = { ...this.config, ...config };
    }
    async read(arrayBuffer) {
        try {
            const uint8Array = new Uint8Array(arrayBuffer);
            // Try to detect if this is a 16-bit PNG and parse accordingly
            const pngInfo = this.detectPngFormat(uint8Array);
            let depthImage;
            if (pngInfo.bitDepth === 16) {
                // Handle 16-bit PNG using raw parsing
                depthImage = await this.parse16BitPng(uint8Array);
            }
            else {
                // Handle 8-bit PNG using canvas fallback
                const imageData = await this.decodePng(uint8Array);
                depthImage = this.convertCanvasToDepth(imageData);
            }
            const metadata = this.createMetadata();
            metadata.bitDepth = pngInfo.bitDepth;
            return { image: depthImage, meta: metadata };
        }
        catch (error) {
            throw new Error(`Failed to read PNG file: ${error instanceof Error ? error.message : String(error)}`);
        }
    }
    async decodePng(data) {
        if (typeof document === 'undefined') {
            return this.decodePngInWorker(data);
        }
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Could not create canvas context'));
                return;
            }
            const blobBuffer = new ArrayBuffer(data.byteLength);
            new Uint8Array(blobBuffer).set(data);
            const blob = new Blob([blobBuffer], { type: 'image/png' });
            const url = URL.createObjectURL(blob);
            const img = new Image();
            img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                try {
                    const imageData = ctx.getImageData(0, 0, img.width, img.height);
                    URL.revokeObjectURL(url);
                    resolve(imageData);
                }
                catch (error) {
                    URL.revokeObjectURL(url);
                    reject(error);
                }
            };
            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load PNG image'));
            };
            img.src = url;
        });
    }
    async decodePngInWorker(data) {
        const blobBuffer = new ArrayBuffer(data.byteLength);
        new Uint8Array(blobBuffer).set(data);
        const blob = new Blob([blobBuffer], { type: 'image/png' });
        const bitmap = await createImageBitmap(blob);
        try {
            const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('Could not create worker canvas context');
            }
            ctx.drawImage(bitmap, 0, 0);
            return ctx.getImageData(0, 0, bitmap.width, bitmap.height);
        }
        finally {
            bitmap.close();
        }
    }
    detectPngFormat(data) {
        // PNG signature check
        const signature = [137, 80, 78, 71, 13, 10, 26, 10];
        for (let i = 0; i < signature.length; i++) {
            if (data[i] !== signature[i]) {
                throw new Error('Invalid PNG signature');
            }
        }
        // Find IHDR chunk (should be the first chunk after signature)
        let offset = 8; // Skip PNG signature
        while (offset < data.length) {
            const length = (data[offset] << 24) |
                (data[offset + 1] << 16) |
                (data[offset + 2] << 8) |
                data[offset + 3];
            const chunkType = String.fromCharCode(data[offset + 4], data[offset + 5], data[offset + 6], data[offset + 7]);
            if (chunkType === 'IHDR') {
                const width = (data[offset + 8] << 24) |
                    (data[offset + 9] << 16) |
                    (data[offset + 10] << 8) |
                    data[offset + 11];
                const height = (data[offset + 12] << 24) |
                    (data[offset + 13] << 16) |
                    (data[offset + 14] << 8) |
                    data[offset + 15];
                const bitDepth = data[offset + 16];
                const colorType = data[offset + 17];
                return { bitDepth, colorType, width, height };
            }
            offset += 8 + length + 4; // chunk header + data + CRC
        }
        throw new Error('IHDR chunk not found');
    }
    async parse16BitPng(data) {
        const pngInfo = this.detectPngFormat(data);
        // Allow grayscale (0) or RGB (2)
        if (pngInfo.colorType !== 0 && pngInfo.colorType !== 2) {
            throw new Error('Only grayscale or RGB PNG images are supported for depth');
        }
        // Full-precision path: the Rust decoder returns the real 16-bit samples.
        // The canvas below can only ever give 8 bits per channel, which quantises
        // millimetre depth maps into ~256 visible steps.
        const wasm = await this.decode16BitPngWasm(data);
        if (wasm) {
            return wasm;
        }
        // Fallback only when the WASM module itself is unavailable.
        const imageData = await this.decodePng(data);
        // Check if this is an RGB image (not grayscale)
        if (Rgb24Converter.isRgbImage(imageData)) {
            console.log(`PNG Reader: Processing 16-bit RGB PNG with fallback to canvas (8-bit precision)`);
            const depthData = Rgb24Converter.convertRgbToDepth(imageData, {
                conversionMode: this.config.rgb24ConversionMode || 'shift',
                scaleFactor: this.config.rgb24ScaleFactor || 1000,
                invalidValue: this.config.rgb24InvalidValue,
            });
            return {
                width: pngInfo.width,
                height: pngInfo.height,
                data: depthData,
            };
        }
        const depthData = new Float32Array(pngInfo.width * pngInfo.height);
        // Since canvas API converts to 8-bit, we need to handle this limitation
        // The scaling factor should account for the original 16-bit range
        console.log(`PNG Reader: Processing 16-bit grayscale PNG with fallback to canvas (8-bit precision)`);
        // Convert 8-bit canvas data to depth values, scaling up to simulate 16-bit range
        for (let i = 0; i < pngInfo.width * pngInfo.height; i++) {
            const pixelIndex = i * 4;
            let rawValue = imageData.data[pixelIndex]; // Use red channel
            // Scale 8-bit value to 16-bit range for better depth representation
            // This partially compensates for precision loss from canvas conversion
            rawValue = rawValue * 256;
            // Check for invalid pixels
            if (rawValue === this.config.invalidValue) {
                depthData[i] = 0;
                continue;
            }
            // Apply scale factor to convert to meters
            const depthValue = rawValue / this.config.pngScaleFactor;
            depthData[i] = depthValue;
        }
        return {
            width: pngInfo.width,
            height: pngInfo.height,
            data: depthData,
        };
    }
    /**
     * Decode a 16-bit PNG through the Rust decoder, keeping the full sample
     * range. Returns null when the WASM module is unavailable so the caller can
     * fall back to the lossy canvas path.
     */
    async decode16BitPngWasm(data) {
        const copy = new ArrayBuffer(data.byteLength);
        new Uint8Array(copy).set(data);
        const png = await decodePng16Wasm(copy);
        if (!png) {
            return null;
        }
        const { width, height, channels, data: samples } = png;
        const pixelCount = width * height;
        const depthData = new Float32Array(pixelCount);
        const scale = this.config.pngScaleFactor || 1;
        const invalid = this.config.invalidValue;
        if (channels >= 3) {
            // RGB-packed 24-bit depth. The packing is defined on 8-bit channels, so
            // take the high byte of each 16-bit sample before combining.
            const rgba = new Uint8ClampedArray(pixelCount * 4);
            for (let i = 0; i < pixelCount; i++) {
                const src = i * channels;
                const dst = i * 4;
                rgba[dst] = samples[src] >> 8;
                rgba[dst + 1] = samples[src + 1] >> 8;
                rgba[dst + 2] = samples[src + 2] >> 8;
                rgba[dst + 3] = 255;
            }
            const imageData = new ImageData(rgba, width, height);
            if (Rgb24Converter.isRgbImage(imageData)) {
                return {
                    width,
                    height,
                    data: Rgb24Converter.convertRgbToDepth(imageData, {
                        conversionMode: this.config.rgb24ConversionMode || 'shift',
                        scaleFactor: this.config.rgb24ScaleFactor || 1000,
                        invalidValue: this.config.rgb24InvalidValue,
                    }),
                };
            }
            // Grayscale replicated across channels: keep channel 0 at 16-bit.
            for (let i = 0; i < pixelCount; i++) {
                const raw = samples[i * channels];
                depthData[i] = raw === invalid ? 0 : raw / scale;
            }
            return { width, height, data: depthData };
        }
        for (let i = 0; i < pixelCount; i++) {
            const raw = samples[i * channels];
            depthData[i] = raw === invalid ? 0 : raw / scale;
        }
        return { width, height, data: depthData };
    }
    convertCanvasToDepth(imageData) {
        const { width, height } = imageData;
        // Check if this is an RGB image (not grayscale)
        if (Rgb24Converter.isRgbImage(imageData)) {
            console.log('[PngReader] Detected RGB image, applying RGB24 conversion');
            const depthData = Rgb24Converter.convertRgbToDepth(imageData, {
                conversionMode: this.config.rgb24ConversionMode || 'shift',
                scaleFactor: this.config.rgb24ScaleFactor || 1000,
                invalidValue: this.config.rgb24InvalidValue,
            });
            return {
                width,
                height,
                data: depthData,
            };
        }
        // Grayscale image
        const { data } = imageData;
        const depthData = new Float32Array(width * height);
        // Convert RGBA to depth values
        // Note: This assumes 8-bit input from canvas, real 16-bit would need different handling
        for (let i = 0; i < width * height; i++) {
            const pixelIndex = i * 4;
            // For grayscale, R, G, B should be the same
            let rawValue = data[pixelIndex]; // Use red channel
            // Check for invalid pixels
            if (rawValue === this.config.invalidValue) {
                depthData[i] = 0; // Mark as invalid
                continue;
            }
            // Apply scale factor to convert to meters
            const depthValue = rawValue / this.config.pngScaleFactor;
            depthData[i] = depthValue;
        }
        return {
            width,
            height,
            data: depthData,
        };
    }
    createMetadata() {
        return {
            kind: 'depth', // Always treat as depth values after scaling
            unit: 'meter', // We convert to meters using pngScaleFactor
            scale: this.config.pngScaleFactor,
            requiresConfiguration: true, // PNG depth images often need configuration
            invalidValue: this.config.invalidValue,
        };
    }
    // Method to configure PNG depth interpretation from UI
    static createWithConfig(config) {
        const reader = new PngReader();
        reader.setConfig(config);
        return reader;
    }
}
// Enhanced version that would use a proper 16-bit PNG library
export class Enhanced16BitPngReader {
    constructor() {
        this.config = {
            pngScaleFactor: 1000, // Default: millimeters to meters
            invalidValue: 0,
        };
    }
    canRead(filename) {
        return filename.toLowerCase().endsWith('.png');
    }
    setConfig(config) {
        this.config = { ...this.config, ...config };
    }
    async read(arrayBuffer) {
        // This would use a library like pngjs to properly decode 16-bit PNG files
        // import PNG from 'pngjs';
        throw new Error('Enhanced 16-bit PNG reader requires pngjs library integration');
        // Example implementation structure:
        /*
            const png = PNG.sync.read(Buffer.from(arrayBuffer));
            
            if (png.colorType !== 0 || png.bitDepth !== 16) {
                throw new Error('PNG must be 16-bit grayscale');
            }
    
            const depthData = new Float32Array(png.width * png.height);
            
            for (let i = 0; i < png.width * png.height; i++) {
                const pixelIndex = i * 2; // 16-bit = 2 bytes per pixel
                const rawValue = (png.data[pixelIndex] << 8) | png.data[pixelIndex + 1];
                
                // Apply depth conversion logic here
                depthData[i] = this.convertRawValueToDepth(rawValue);
            }
    
            return {
                image: { width: png.width, height: png.height, data: depthData },
                meta: this.createMetadata()
            };
            */
    }
}
